/**
 * LiveKit WebSocket-URL för Qvenari-klienten (jakt / join).
 * Primärt ska Qmeet-svaret från POST .../join (`livekit_url`) användas — då följer klienten Cloud Run-env (LIVEKIT_PUBLIC_URL).
 * Denna modul är bara fallback när svaret saknar fältet eller för äldre API.
 */

const fromEnv = () => (import.meta.env.VITE_LIVEKIT_WS_URL as string | undefined)?.trim() || '';

function isLocalhostHost(host: string): boolean {
  return host === 'localhost' || host === '127.0.0.1';
}

function isQvenariProdWeb(host: string): boolean {
  return host === 'qvenari.com' || host === 'www.qvenari.com';
}

function isPrivateOrLoopbackHost(host: string): boolean {
  if (isLocalhostHost(host)) return true;
  if (host.startsWith('100.64.')) return true;
  if (host.startsWith('192.168.')) return true;
  if (host.startsWith('10.')) return true;
  const m = host.match(/^172\.(\d+)\./);
  if (m) {
    const n = Number(m[1]);
    if (n >= 16 && n <= 31) return true;
  }
  return false;
}

/**
 * På HTTPS får aldrig `ws://` användas (mixed content). Ersätter legacy Tailscale/privata värdar
 * med `VITE_LIVEKIT_WS_URL` eller samma fallback som `clientLivekitWsUrlFallback`.
 * Uppgraderar övriga `ws://` till `wss://` och tar bort :7880 om Caddy lyssnar på 443.
 */
export function coerceLivekitUrlForBrowser(url: string): string {
  if (typeof window === 'undefined' || !url?.trim()) return url;
  const trimmed = url.trim();
  let u: URL;
  try {
    u = new URL(trimmed);
  } catch {
    return trimmed;
  }

  if (window.location.protocol !== 'https:') return trimmed;

  if (u.protocol === 'ws:') {
    const host = u.hostname;
    if (isPrivateOrLoopbackHost(host)) {
      const env = fromEnv();
      if (env) return env;
      return clientLivekitWsUrlFallback(false);
    }
    u.protocol = 'wss:';
    if (u.port === '7880') u.port = '';
    return u.href.replace(/\/$/, '');
  }

  return trimmed;
}

/**
 * Fallback när Qmeet inte skickar `livekit_url` i join-svaret.
 * Sätt `VITE_LIVEKIT_WS_URL` i produktion till er publika WSS (t.ex. wss://livekit.erdomän.se).
 */
export function clientLivekitWsUrlFallback(isCapacitor: boolean): string {
  const env = fromEnv();
  if (env) return env;

  const capOnly = (import.meta.env.VITE_CAPACITOR_LIVEKIT_WS_URL as string | undefined)?.trim();
  if (isCapacitor && capOnly) return capOnly;

  if (typeof window === 'undefined') return 'ws://localhost:7880';

  const host = window.location.hostname;
  const secure = window.location.protocol === 'https:';

  if (isCapacitor) {
    // Utan Tailscale: samma publika WSS som webben (kräver att LiveKit nås därifrån).
    return 'wss://ws.qvenari.com';
  }
  if (isQvenariProdWeb(host)) return 'wss://ws.qvenari.com';
  if (secure) return `wss://${host}:7443`;
  if (isLocalhostHost(host)) return 'ws://localhost:7880';
  return `ws://${host}:7880`;
}

/** Om Qmeet returnerar `livekit_url`, använd den — men tvinga säker URL på HTTPS-sidor. */
export function resolveLivekitWsUrl(joinResponse: { livekit_url?: string } | undefined, isCapacitor: boolean): string {
  const u = joinResponse?.livekit_url;
  const raw = u && typeof u === 'string' && u.trim() ? u.trim() : clientLivekitWsUrlFallback(isCapacitor);
  return coerceLivekitUrlForBrowser(raw);
}
